import { todayKSTDate } from './kiwoomTodayResult';

/**
 * Day trading paper — theme-후발-dip 지정가 전략 (tp3 · N=5).
 *
 * 스냅샷은 pairs paper를 미러링하되, Perpetual Live처럼 거래 단위 fills·
 * equity_curve·일별 rows를 같이 실어 준다.
 */

export const KIWOOM_DT_START_EQUITY = 1_000_000;

/** analyser 프록시 경로. pairs와 대칭 (`/mercury-analyser-kiwoom/pairs-paper`). */
export function kiwoomDayTradingPaperApiUrl(tradeDate?: string): string {
  const base = '/mercury-analyser-kiwoom/daytrading-paper';
  return tradeDate ? `${base}?date=${encodeURIComponent(tradeDate)}` : base;
}

export type KiwoomDtDay = {
  date: string;
  signals: number;
  taken: number;
  skipped?: number;
  wins: number;
  pnl_krw: number;
  ret_pct: number;
  equity_krw: number;
};

export type KiwoomDtSummary = {
  final_equity_krw: number;
  total_ret_pct: number;
  win_rate: number;
  pos_days: number;
  n_days: number;
  mdd_pct: number;
  peak_equity_krw?: number;
  drawdown_pct?: number;
  taken?: number;
  wins?: number;
  skipped?: number;
  today_pnl_krw?: number;
  today_date?: string | null;
};

export type KiwoomDtFill = {
  time?: string;
  exit_time?: string;
  day?: string;
  symbol: string;
  side?: string;
  price?: number;
  quantity?: number;
  alloc_krw?: number;
  net_pct?: number;
  hold_min?: number;
  rule?: string;
  pnl_krw?: number | null;
  status?: string;
};

export type KiwoomDtCurvePoint = {
  n: number;
  date?: string | null;
  equity: number;
};

export type KiwoomDtConfig = {
  rule: string;
  n: number;
  dip_pct: number;
  crt_max: number;
  fee_pct: number;
};

export type KiwoomDtSegment = {
  start_date?: string;
  from?: string;
  to?: string;
  days: KiwoomDtDay[];
  summary: KiwoomDtSummary;
  equity_krw?: number;
  cash_krw?: number;
  realized_pnl_krw?: number;
  fills?: KiwoomDtFill[];
  fills_today?: KiwoomDtFill[];
  equity_curve?: KiwoomDtCurvePoint[];
  open?: unknown[];
};

export type KiwoomDayTradingSnapshot = {
  strategy?: string;
  mode?: string;
  config?: KiwoomDtConfig;
  start_equity_krw?: number;
  backtest?: KiwoomDtSegment;
  live?: KiwoomDtSegment;
  updated_at?: string;
};

/** 고정 상수 — 2026-08-03~09-23, 14거래일, tp3 N=5 (OCI 시뮬 실측). */
export const KIWOOM_DT_CONFIG: KiwoomDtConfig = {
  rule: 'tp3',
  n: 5,
  dip_pct: 1.0,
  crt_max: 3.1,
  fee_pct: 0.23,
};

/** 번들 백테스트(in-sample) — 백엔드 스냅샷이 없을 때의 컨텍스트. fills는 API 우선. */
export const KIWOOM_DT_BACKTEST: KiwoomDtSegment = {
  from: '2026-08-03',
  to: '2026-09-23',
  days: [
    { date: '2026-08-03', signals: 13, taken: 10, skipped: 3, wins: 9, pnl_krw: 47046, ret_pct: 4.70, equity_krw: 1047046 },
    { date: '2026-08-04', signals: 11, taken: 5, skipped: 6, wins: 3, pnl_krw: 3939, ret_pct: 0.38, equity_krw: 1050985 },
    { date: '2026-08-05', signals: 8, taken: 6, skipped: 2, wins: 4, pnl_krw: 23591, ret_pct: 2.24, equity_krw: 1074576 },
    { date: '2026-08-06', signals: 18, taken: 9, skipped: 9, wins: 6, pnl_krw: 14228, ret_pct: 1.32, equity_krw: 1088804 },
    { date: '2026-08-07', signals: 3, taken: 3, skipped: 0, wins: 1, pnl_krw: -15010, ret_pct: -1.38, equity_krw: 1073794 },
    { date: '2026-08-10', signals: 9, taken: 6, skipped: 3, wins: 5, pnl_krw: 23103, ret_pct: 2.15, equity_krw: 1096897 },
    { date: '2026-08-11', signals: 5, taken: 5, skipped: 0, wins: 4, pnl_krw: 20646, ret_pct: 1.88, equity_krw: 1117543 },
    { date: '2026-08-12', signals: 8, taken: 7, skipped: 1, wins: 5, pnl_krw: 13294, ret_pct: 1.19, equity_krw: 1130837 },
    { date: '2026-08-13', signals: 8, taken: 5, skipped: 3, wins: 3, pnl_krw: 16110, ret_pct: 1.42, equity_krw: 1146947 },
    { date: '2026-08-14', signals: 7, taken: 6, skipped: 1, wins: 2, pnl_krw: -7887, ret_pct: -0.69, equity_krw: 1139060 },
    { date: '2026-08-18', signals: 4, taken: 4, skipped: 0, wins: 4, pnl_krw: 27508, ret_pct: 2.41, equity_krw: 1166568 },
    { date: '2026-08-19', signals: 5, taken: 5, skipped: 0, wins: 3, pnl_krw: 10911, ret_pct: 0.94, equity_krw: 1177479 },
    { date: '2026-09-21', signals: 8, taken: 8, skipped: 0, wins: 8, pnl_krw: 54754, ret_pct: 4.65, equity_krw: 1232233 },
    { date: '2026-09-23', signals: 4, taken: 4, skipped: 0, wins: 3, pnl_krw: 19558, ret_pct: 1.59, equity_krw: 1251791 },
  ],
  summary: {
    final_equity_krw: 1251791,
    total_ret_pct: 25.18,
    win_rate: 0.72,
    pos_days: 12,
    n_days: 14,
    mdd_pct: -1.4,
    peak_equity_krw: 1251791,
    drawdown_pct: 0,
    taken: 83,
    wins: 60,
    skipped: 28,
    today_pnl_krw: 19558,
    today_date: '2026-09-23',
  },
  equity_krw: 1251791,
  cash_krw: 1251791,
  realized_pnl_krw: 251791,
  fills: [],
  fills_today: [],
  equity_curve: [
    { n: 0, date: null, equity: 1_000_000 },
    { n: 1, date: '2026-08-03', equity: 1047046 },
    { n: 2, date: '2026-08-04', equity: 1050985 },
    { n: 3, date: '2026-08-05', equity: 1074576 },
    { n: 4, date: '2026-08-06', equity: 1088804 },
    { n: 5, date: '2026-08-07', equity: 1073794 },
    { n: 6, date: '2026-08-10', equity: 1096897 },
    { n: 7, date: '2026-08-11', equity: 1117543 },
    { n: 8, date: '2026-08-12', equity: 1130837 },
    { n: 9, date: '2026-08-13', equity: 1146947 },
    { n: 10, date: '2026-08-14', equity: 1139060 },
    { n: 11, date: '2026-08-18', equity: 1166568 },
    { n: 12, date: '2026-08-19', equity: 1177479 },
    { n: 13, date: '2026-09-21', equity: 1232233 },
    { n: 14, date: '2026-09-23', equity: 1251791 },
  ],
  open: [],
};

/** 실전 페이퍼 시드 — 첫 세션 전(추석 연휴 후 재개 대기). */
export function seedLiveSegment(): KiwoomDtSegment {
  return {
    start_date: '2026-09-26',
    days: [],
    summary: {
      final_equity_krw: KIWOOM_DT_START_EQUITY,
      total_ret_pct: 0,
      win_rate: 0,
      pos_days: 0,
      n_days: 0,
      mdd_pct: 0,
      peak_equity_krw: KIWOOM_DT_START_EQUITY,
      drawdown_pct: 0,
      taken: 0,
      wins: 0,
      skipped: 0,
      today_pnl_krw: 0,
      today_date: null,
    },
    equity_krw: KIWOOM_DT_START_EQUITY,
    cash_krw: KIWOOM_DT_START_EQUITY,
    realized_pnl_krw: 0,
    fills: [],
    fills_today: [],
    equity_curve: [{ n: 0, date: null, equity: KIWOOM_DT_START_EQUITY }],
    open: [],
  };
}

export async function fetchDayTradingPaper(
  tradeDate?: string,
): Promise<KiwoomDayTradingSnapshot | null> {
  const res = await fetch(kiwoomDayTradingPaperApiUrl(tradeDate), { cache: 'no-store' });
  if (res.status === 404) return null;
  const text = await res.text();
  if (!res.ok || !text.trim() || text.trimStart().startsWith('<')) return null;
  return JSON.parse(text) as KiwoomDayTradingSnapshot;
}

/** 스냅샷(있으면)과 번들 컨텍스트를 합쳐 렌더에 쓸 세그먼트를 만든다. */
export function resolveSegments(snap: KiwoomDayTradingSnapshot | null): {
  backtest: KiwoomDtSegment;
  live: KiwoomDtSegment;
  updatedAt: string | null;
} {
  return {
    backtest: snap?.backtest ?? KIWOOM_DT_BACKTEST,
    live: snap?.live ?? seedLiveSegment(),
    updatedAt: snap?.updated_at ?? null,
  };
}

export function hmOf(iso?: string | null): string {
  if (!iso) return '–';
  // "2026-08-03 09:04:05" or ISO
  const m = iso.match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : iso.slice(11, 16) || '–';
}

export { todayKSTDate };
